import { HttpErrorResponse } from '@angular/common/http';
import { Action, createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { SurveySummary } from '../interfaces/survey-summary.interface';
import { Survey } from '../interfaces/survey.interface';
import { UserSurveyResponse } from '../interfaces/user-survey-response.interface';
import { UserSurveyStatus, UserSurveyStatusDto } from '../interfaces/user-survey-status.interface';

export const createSurvey = createActionGroup({
    source: '[Aumico-Core] Create Survey',
    events: {
        'Create Survey': emptyProps(),
        'Create Survey Success': props<{ payload: Survey }>(),
        'Create Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const deleteSurvey = createActionGroup({
    source: '[Aumico-Core] Delete Survey',
    events: {
        'Delete Survey': props<{ payload: string }>(),
        'Delete Survey Success': emptyProps(),
        'Delete Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});
export const downloadSurveyCsv = createActionGroup({
    source: '[Aumico-Core] Download Survey Csv',
    events: {
        'Download Survey Csv': props<{ payload: string }>(),
        'Download Survey Csv Success': emptyProps(),
        'Download Survey Csv Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const stopSurvey = createActionGroup({
    source: '[Aumico-Core] Stop Survey',
    events: {
        'Stop Survey': props<{ payload: string }>(),
        'Stop Survey Success': props<{ payload: Survey }>(),
        'Stop Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getActiveSurvey = createActionGroup({
    source: '[Aumico-Core] Get Active Survey',
    events: {
        'Get Active Survey': emptyProps(),
        'Get Active Survey Success': props<{ payload: Survey }>(),
        'Get Active Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getSurveyById = createActionGroup({
    source: '[Aumico-Core] Get Survey By Id',
    events: {
        'Get Survey By Id': props<{ payload: string }>(),
        'Get Survey By Id Success': props<{ payload: Survey }>(),
        'Get Survey By Id Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getEligibleUsersSurveyStatus = createActionGroup({
    source: '[Aumico-Core] Get Eligible Users Survey Status',
    events: {
        'Get Eligible Users Survey Status': props<{ payload: string }>(),
        'Get Eligible Users Survey Status Success': props<{ payload: UserSurveyStatus[] }>(),
        'Get Eligible Users Survey Status Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const checkUserEligibility = createActionGroup({
    source: '[Aumico-Core] Check User Eligibility',
    events: {
        'Check User Eligibility': props<{ payload: string }>(),
        'Check User Eligibility Success': props<{ payload: boolean }>(),
        'Check User Eligibility Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const saveResponse = createActionGroup({
    source: '[Aumico-Core] Save Response',
    events: {
        'Save Response': props<{ surveyId: string; questionNumber: number; response: string }>(),
        'Save Response Success': props<{ questionNumber: number; response: string }>(),
        'Save Response Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const dismissSurvey = createActionGroup({
    source: '[Aumico-Core] Dismiss Survey',
    events: {
        'Dismiss Survey': props<{ payload: string }>(),
        'Dismiss Survey Success': props<{ payload: UserSurveyStatus }>(),
        'Dismiss Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const completeSurvey = createActionGroup({
    source: '[Aumico-Core] Complete Survey',
    events: {
        'Complete Survey': props<{ payload: string }>(),
        'Complete Survey Success': props<{ payload: UserSurveyStatus }>(),
        'Complete Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const startSurvey = createActionGroup({
    source: '[Aumico-Core] Start Survey',
    events: {
        'Start Survey': props<{ payload: string }>(),
        'Start Survey Success': props<{ payload: UserSurveyStatus }>(),
        'Start Survey Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getUserSurveyStatus = createActionGroup({
    source: '[Aumico-Core] Get User Survey Status',
    events: {
        'Get User Survey Status': props<{ payload: string }>(),
        'Get User Survey Status Success': props<{ payload: UserSurveyStatusDto | null }>(),
        'Get User Survey Status Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getUsersToNotify = createActionGroup({
    source: '[Aumico-Core] Get Users To Notify',
    events: {
        'Get Users To Notify': props<{ payload: string }>(),
        'Get Users To Notify Success': props<{ payload: string[] }>(),
        'Get Users To Notify Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const markUserAsNotified = createActionGroup({
    source: '[Aumico-Core] Mark User As Notified',
    events: {
        'Mark User As Notified': props<{ payload: string }>(),
        'Mark User As Notified Success': props<{ payload: UserSurveyStatus }>(),
        'Mark User As Notified Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const showSurveyPopup = createActionGroup({
    source: '[Aumico-Core] Show Survey Popup',
    events: {
        'Show Survey Popup': emptyProps(),
        'Show Survey Popup Success': emptyProps(),
        'Show Survey Popup Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const showSurveyFlag = createActionGroup({
    source: '[Aumico-Core] Show Survey Flag',
    events: {
        'Show Survey Flag': emptyProps(),
        'Show Survey Flag Success': emptyProps(),
        'Show Survey Flag Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const nextQuestion = createActionGroup({
    source: '[Aumico-Core] Next Question',
    events: {
        'Next Question': emptyProps(),
    },
});

export const previousQuestion = createActionGroup({
    source: '[Aumico-Core] Previous Question',
    events: {
        'Previous Question': emptyProps(),
    },
});

export const getSurveyResponses = createActionGroup({
    source: '[Aumico-Core] Get Survey Responses',
    events: {
        'Get Survey Responses': props<{ payload: string }>(),
        'Get Survey Responses Success': props<{ payload: UserSurveyResponse[] }>(),
        'Get Survey Responses Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getAllSurveys = createActionGroup({
    source: '[Aumico-Core] Get All Surveys',
    events: {
        'Get All Surveys': emptyProps(),
        'Get All Surveys Success': props<{ payload: SurveySummary[] }>(),
        'Get All Surveys Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getOpenedSurveySummary = createAction(
    '[Aumico-Core] Get Opened Survey Summary',
    props<{
        payload: SurveySummary;
    }>(),
);

export const resetQuestionNumber = createAction('[Aumico-Core] Reset Question Number');

export interface ActionWithPayload<T> extends Action {
    payload: T;
}
